import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { recordPick, completePickSession } from "@/lib/fulfillment.functions";
import { getPickSession, listPickSessionLines, markPickLineShort, bumpPickLineSequence, assignToteToOrder, abandonPickSession, assignSharedBin, assignToteToSku } from "@/lib/pick.functions";
import { requestLocationCycleCount } from "@/lib/cycle-counts.functions";
import { getOrderStatusesLite } from "@/lib/operations.functions";
import { runServerFn } from "@/lib/server-fn";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { ScanInput } from "@/components/ui/scan-input";
import { StickyActionBar } from "@/components/ui/sticky-action-bar";
import { StatusPill } from "@/components/ui/status-pill";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2, ChevronRight, Loader2, MapPin, Package, ListOrdered, AlertTriangle, SkipForward, Boxes, Undo2, ArrowLeft, Flag, ClipboardCheck,
  Ban,
} from "lucide-react";
import { toast } from "sonner";
import { scannerBeep } from "@/hooks/use-scanner";

export const Route = createFileRoute("/_authenticated/app/pick/session/$sessionId")({ component: PickSession });

function PickSession() {
  const { sessionId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const recordPickFn = useServerFn(recordPick);
  const completeSessionFn = useServerFn(completePickSession);
  const getSessionFn = useServerFn(getPickSession);
  const listLinesFn = useServerFn(listPickSessionLines);
  const markShortFn = useServerFn(markPickLineShort);
  const bumpSeqFn = useServerFn(bumpPickLineSequence);
  const assignToteFn = useServerFn(assignToteToOrder);
  const assignSharedBinFn = useServerFn(assignSharedBin);
  const assignToteToSkuFn = useServerFn(assignToteToSku);
  const abandonFn = useServerFn(abandonPickSession);
  const requestCountFn = useServerFn(requestLocationCycleCount);
  const statusLiteFn = useServerFn(getOrderStatusesLite);
  const [busy, setBusy] = useState(false);
  const [shortOpen, setShortOpen] = useState(false);
  const [shortQty, setShortQty] = useState("0");
  const [shortReason, setShortReason] = useState("");
  const [toteValue, setToteValue] = useState("");
  // For order-by-order picking: tote assigned per order in the session.
  const [toteByOrder, setToteByOrder] = useState<Record<string, string>>({});

  const { data: session } = useQuery({
    queryKey: ["pick-session", sessionId],
    queryFn: () => getSessionFn({ data: { sessionId } }),
  });

  const { data: lines = [], refetch } = useQuery({
    queryKey: ["pick-session-lines", sessionId],
    queryFn: () => listLinesFn({ data: { sessionId } }),
  });

  // Distinct order IDs on this session for cancellation polling.
  const sessionOrderIds = useMemo(() => {
    const set = new Set<string>();
    for (const l of lines as any[]) if (l?.order_id) set.add(l.order_id);
    return Array.from(set);
  }, [lines]);

  const { data: statusLite, refetch: refetchStatuses } = useQuery({
    queryKey: ["session-order-statuses", sessionId, sessionOrderIds.join(",")],
    queryFn: () => statusLiteFn({ data: { orderIds: sessionOrderIds } }),
    enabled: sessionOrderIds.length > 0,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const cancelledOrders = useMemo(() => {
    const map = statusLite?.statuses ?? {};
    return sessionOrderIds.filter((id) => map[id] === "cancelled");
  }, [statusLite, sessionOrderIds]);

  const [acknowledgedCancellations, setAcknowledgedCancellations] = useState<Set<string>>(new Set());
  const [confirmAbandonOpen, setConfirmAbandonOpen] = useState(false);
  const [countMenuOpen, setCountMenuOpen] = useState(false);
  const unacknowledgedCancellations = cancelledOrders.filter((id) => !acknowledgedCancellations.has(id));
  const blockingCancellation = unacknowledgedCancellations.length > 0;

  useEffect(() => {
    const tenantId = (session as any)?.tenant_id;
    if (!tenantId) return;
    // Realtime stays on browser client — no server-fn equivalent.
    const ch = supabase
      .channel(`t:${tenantId}:ps:${sessionId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "pick_session_lines", filter: `session_id=eq.${sessionId}` }, () => refetch())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [sessionId, refetch, (session as any)?.tenant_id]);

  const remaining = useMemo(() => lines.filter((l: any) => l.qty_picked < l.qty_required), [lines]);
  const toteMode = (session?.tote_mode ?? undefined) as
    | "single_bin" | "tote_per_sku" | "tote_per_order" | undefined;
  const isBatch = session?.strategy === "batch";
  const isSingleBin = toteMode === "single_bin";
  const isTotePerSku = toteMode === "tote_per_sku";
  const isTotePerOrder = isBatch && toteMode === "tote_per_order";
  const sharedToteCode: string | undefined = (session as any)?.shared_tote?.code;

  // For batch picking: group remaining lines by SKU + location so the
  // operator picks all "like" units together (e.g. 6 of BL-COF-DCAF-12
  // across 3 orders, then 6 of BL-COF-ETH-12).
  const batchGroups = useMemo(() => {
    if (!isBatch) return [] as any[];
    const map = new Map<string, any>();
    for (const l of remaining) {
      const key = `${l.sku_id}|${l.from_location_id ?? ""}`;
      const need = l.qty_required - l.qty_picked;
      const g = map.get(key);
      if (g) {
        g.qty += need;
        g.lines.push(l);
        g.orderIds.add(l.order_id);
      } else {
        map.set(key, {
          key,
          sku: l.sku,
          location: l.location,
          lot: l.lot,
          qty: need,
          lines: [l],
          orderIds: new Set([l.order_id]),
          sortSeq: l.pick_sequence ?? 0,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.sortSeq - b.sortSeq);
  }, [isBatch, remaining]);
  const currentBatch: any = batchGroups[0];

  // Orders in this session that still need a tote assigned (batch mode).
  const ordersNeedingTote = useMemo(() => {
    if (!isTotePerOrder) return [] as any[];
    const seen = new Map<string, any>();
    for (const l of lines) {
      if (!l.order_id || seen.has(l.order_id)) continue;
      const code = l.tote?.code || toteByOrder[l.order_id];
      if (!code) {
        seen.set(l.order_id, { orderId: l.order_id, orderNumber: l.order?.order_number });
      }
    }
    return Array.from(seen.values());
  }, [isBatch, lines, toteByOrder]);

  const current: any = isBatch ? currentBatch?.lines?.[0] : remaining[0];
  const totalReq = lines.reduce((s: number, l: any) => s + l.qty_required, 0);
  const totalPicked = lines.reduce((s: number, l: any) => s + l.qty_picked, 0);
  const lineHasTote = !!current?.to_tote_id;
  const requiresTote = session?.strategy === "pick_to_bin" && lineHasTote;
  const currentOrderId: string | undefined = current?.order_id;
  const assignedTote = currentOrderId ? toteByOrder[currentOrderId] : undefined;
  // Order-by-order ("discrete"): every time we land on the first line of a
  // new order, prompt to scan/assign a tote before picking starts.
  const needsToteAssignment =
    !!current && session?.strategy === "discrete" && !assignedTote && !lineHasTote;
  // W3 (tote per order): assign all order totes upfront before any picking.
  const needsBatchToteSetup = isTotePerOrder && ordersNeedingTote.length > 0 && !!currentBatch;
  // W1 (single bin): scan one bin before any picking starts.
  const needsSharedBin = isSingleBin && !sharedToteCode && !!currentBatch;
  // W2 (tote per SKU): if the current SKU has no tote yet, prompt for one.
  const needsSkuTote = isTotePerSku && !!currentBatch && !currentBatch.lines[0]?.to_tote_id;
  const currentSkuToteCode: string | undefined = currentBatch?.lines?.[0]?.tote?.code;

  // Hydrate from already-stamped lines (e.g. resuming a session).
  useEffect(() => {
    if (currentOrderId && !toteByOrder[currentOrderId] && current?.tote?.code) {
      setToteByOrder((m) => ({ ...m, [currentOrderId]: current.tote.code }));
    }
  }, [currentOrderId, current?.tote?.code, toteByOrder]);

  const recordOne = async (skuScan?: string) => {
    if (!current) return;
    if (requiresTote && toteValue.trim().toUpperCase() !== (current as any).tote?.code?.toUpperCase()) {
      scannerBeep(false);
      return toast.error("Wrong tote — scan the assigned tote");
    }
    if (needsToteAssignment) {
      scannerBeep(false);
      return toast.error("Assign a tote to this order first");
    }
    setBusy(true);
    try {
      await runServerFn(
        () => recordPickFn({
          data: {
            sessionLineId: current.id,
            qty: 1,
            toteCode: requiresTote ? toteValue : (assignedTote || null),
            skuScan: skuScan ?? null,
          },
        }),
        "Pick",
      );
      scannerBeep(true);
      refetch();
    } catch {
      scannerBeep(false);
    } finally {
      setBusy(false);
    }
  };

  // Batch: record the full grouped qty across all underlying order lines.
  const recordBatchGroup = async (skuScan?: string) => {
    if (!currentBatch) return;
    setBusy(true);
    try {
      for (const l of currentBatch.lines) {
        const need = l.qty_required - l.qty_picked;
        if (need <= 0) continue;
        const toteCode = l.tote?.code || toteByOrder[l.order_id] || null;
        await runServerFn(
          () => recordPickFn({
            data: {
              sessionLineId: l.id,
              qty: need,
              toteCode,
              skuScan: skuScan ?? null,
            },
          }),
          "Pick",
        );
      }
      scannerBeep(true);
      refetch();
    } catch {
      scannerBeep(false);
    } finally {
      setBusy(false);
    }
  };

  const assignToteForCurrentOrder = async (rawCode: string) => {
    const code = rawCode.trim();
    if (!code || !currentOrderId) return;
    setBusy(true);
    try {
      await runServerFn(
        () => assignToteFn({ data: { sessionId, orderId: currentOrderId, code } }),
        "Assign tote",
      );
      setToteByOrder((m) => ({ ...m, [currentOrderId]: code }));
      scannerBeep(true);
      toast.success(`Tote ${code} assigned to this order`);
      refetch();
    } catch {
      scannerBeep(false);
    } finally {
      setBusy(false);
    }
  };

  const handleScan = (raw: string) => {
    if (!current) return;
    if (blockingCancellation) {
      scannerBeep(false);
      return toast.error("An order on this session was cancelled — acknowledge it first");
    }
    if (needsToteAssignment) {
      scannerBeep(false);
      return toast.error("Assign a tote to this order first");
    }
    const expected = (current.sku?.sku_code ?? "").toLowerCase();
    const expectedBarcode = (current.sku?.barcode ?? "").toLowerCase();
    const v = raw.trim().toLowerCase();
    if (expected && v !== expected && v !== expectedBarcode) {
      scannerBeep(false);
      return toast.error(`Expected ${current.sku?.sku_code}`);
    }
    if (isBatch) recordBatchGroup(raw.trim());
    else recordOne(raw.trim());
  };

  const submitShort = async () => {
    const n = Math.max(0, Math.min(Number(shortQty) || 0, current.qty_required - current.qty_picked));
    setBusy(true);
    if (n > 0) {
      try {
        await runServerFn(
          () => recordPickFn({ data: { sessionLineId: current.id, qty: n } }),
          "Short pick",
        );
      } catch {
        setBusy(false);
        return;
      }
    }
    try {
      await runServerFn(
        () => markShortFn({ data: { lineId: current.id, qtyRequired: current.qty_required } }),
        "Mark short",
      );
    } catch {
      setBusy(false);
      return;
    }
    setBusy(false);
    toast.warning(`Marked short on ${current.sku?.sku_code}${shortReason ? ` — ${shortReason}` : ""}`);
    setShortOpen(false); setShortQty("0"); setShortReason("");
    refetch();
  };

  const complete = async () => {
    try {
      await runServerFn(
        () => completeSessionFn({ data: { sessionId } }),
        "Complete session",
      );
      toast.success("Session complete");
      qc.invalidateQueries({ queryKey: ["pick-eligible"] });
      qc.invalidateQueries({ queryKey: ["open-pick-jobs"] });
      navigate({ to: "/app/pick" });
    } catch {
      // toast already shown by runServerFn
    }
  };

  // Auto-complete the session as soon as every line is fully picked.
  // Without this the operator has to tap "Complete session" manually, and
  // orders sit in "picked" instead of being released for packing.
  const autoCompletedRef = useRef(false);
  useEffect(() => {
    if (autoCompletedRef.current) return;
    if (!lines.length) return;
    if (totalReq === 0) return;
    if (totalPicked < totalReq) return;
    if (session?.status === "completed" || session?.status === "cancelled") return;
    autoCompletedRef.current = true;
    complete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.length, totalPicked, totalReq, session?.status]);

  const abandon = async () => {
    const msg = totalPicked > 0
      ? `${totalPicked} unit(s) already picked. Short or complete them first — undo is only allowed before any picks.`
      : "Cancel this session and put every order on it back to allocated?";
    if (totalPicked > 0) { toast.error(msg); return; }
    setConfirmAbandonOpen(true);
  };

  const confirmAbandon = async () => {
    setConfirmAbandonOpen(false);
    try {
      await runServerFn(
        () => abandonFn({ data: { sessionId } }),
        "Undo session",
      );
      toast.success("Session cancelled · orders reset to allocated");
      qc.invalidateQueries({ queryKey: ["pick-eligible"] });
      qc.invalidateQueries({ queryKey: ["pick-session", sessionId] });
      navigate({ to: "/app/pick" });
    } catch { /* runServerFn already toasted */ }
  };

  // "Untouched" session = operator just opened it but never assigned a tote
  // and never picked anything. In that case the primary action becomes a
  // plain Back (pure navigation, no release, no order state changes) so a
  // worker can bail out of a session they only viewed. Once real state
  // exists, the button reverts to the destructive "Undo session" flow.
  const anyToteAssigned =
    !!sharedToteCode ||
    !!currentSkuToteCode ||
    Object.keys(toteByOrder).length > 0 ||
    (lines as any[]).some((l) => !!l.tote?.code || !!l.to_tote_id);
  const sessionUntouched = totalPicked === 0 && !anyToteAssigned;

  const goBack = () => {
    if (!sessionUntouched) {
      // Real picking state exists — keep the destructive confirm flow.
      abandon();
      return;
    }
    // Pure navigation. Never mutate session/order state from Back.
    // Prefer the worker's prior in-app page; if there is no usable
    // history (deep link, fresh tab), fall back to Worker Workspace
    // home so we never strand them outside the workspace.
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
      return;
    }
    navigate({ to: "/app/rf" });
  };

  // Worker-initiated cycle count from the pick screen.
  // Two paths: "Flag" (queue it for later) and "Count now" (claim + open).
  const requestCountForCurrent = async (opts: { claim: boolean; reason?: string }) => {
    if (!current) return;
    const tenantId = (session as any)?.tenant_id;
    const facilityId = (session as any)?.facility_id;
    const locationId = current.location?.id ?? current.location_id;
    const skuId = current.sku?.id ?? current.sku_id;
    if (!tenantId || !facilityId || !locationId) {
      toast.error("Missing location context");
      return;
    }
    try {
      const res = await runServerFn(
        () =>
          requestCountFn({
            data: {
              tenantId,
              facilityId,
              locationId,
              skuId: skuId ?? null,
              lot: current.lot ?? null,
              source: "pick_session",
              reason: opts.reason ?? null,
              claim: opts.claim,
            },
          }),
        opts.claim ? "Start count" : "Flag location",
      );
      if (opts.claim) {
        toast.success(`Counting ${current.location?.code ?? "location"}`);
        navigate({ to: "/app/rf/cycle-count/$id", params: { id: res.id } });
      } else {
        toast.success(`Flagged ${current.location?.code ?? "location"} for cycle count`);
      }
      setCountMenuOpen(false);
    } catch { /* runServerFn toasted */ }
  };

  const pct = totalReq ? (totalPicked / totalReq) * 100 : 0;

  return (
    <>
      <AlertDialog open={confirmAbandonOpen} onOpenChange={setConfirmAbandonOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this session?</AlertDialogTitle>
            <AlertDialogDescription>
              Every order on this session will be put back to allocated. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep session</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAbandon}>Cancel session</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={blockingCancellation}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Ban className="h-5 w-5" />
              {unacknowledgedCancellations.length === 1
                ? "Order cancelled mid-pick"
                : `${unacknowledgedCancellations.length} orders cancelled mid-pick`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              The following order{unacknowledgedCancellations.length === 1 ? " was" : "s were"} cancelled
              while you were picking. Stop picking for {unacknowledgedCancellations.length === 1 ? "it" : "them"} —
              already-picked units stay in your tote until a putaway task is generated for the source location.
            </p>
            <ul className="rounded-md border divide-y bg-muted/30">
              {unacknowledgedCancellations.map((id) => {
                const ol = (lines as any[]).find((l) => l.order_id === id);
                const num = ol?.order?.order_number ?? id.slice(0, 8);
                const picked = (lines as any[])
                  .filter((l) => l.order_id === id)
                  .reduce((s, l) => s + (l.qty_picked || 0), 0);
                return (
                  <li key={id} className="px-3 py-2 flex items-center justify-between gap-3">
                    <span className="font-mono text-xs">{num}</span>
                    <span className="text-xs text-muted-foreground">{picked} unit{picked === 1 ? "" : "s"} already picked</span>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-muted-foreground">
              Acknowledging will skip remaining lines for {unacknowledgedCancellations.length === 1 ? "this order" : "these orders"} in the queue.
              Visit the order detail to mark items back to source.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={async () => {
                // Skip remaining session lines for cancelled orders by bumping their sequence to the end.
                const max = Math.max(0, ...((lines as any[]).map((l) => l.pick_sequence ?? 0)));
                let n = max + 1;
                for (const id of unacknowledgedCancellations) {
                  const pending = (lines as any[]).filter(
                    (l) => l.order_id === id && l.qty_picked < l.qty_required,
                  );
                  for (const l of pending) {
                    try {
                      await runServerFn(
                        () => bumpSeqFn({ data: { lineId: l.id, sequence: n++ } }),
                        "Skip cancelled",
                      );
                    } catch { /* keep going */ }
                    try {
                      await runServerFn(
                        () => markShortFn({ data: { lineId: l.id, qtyRequired: l.qty_required } }),
                        "Skip cancelled",
                      );
                    } catch { /* keep going */ }
                  }
                }
                setAcknowledgedCancellations((prev) => {
                  const next = new Set(prev);
                  for (const id of unacknowledgedCancellations) next.add(id);
                  return next;
                });
                refetch();
                refetchStatuses();
              }}
            >
              Acknowledge &amp; skip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PageHeader
        title="Pick Session"
        description={`${totalPicked}/${totalReq} units · ${
          isSingleBin ? "Bulk · 1 tote"
          : isTotePerSku ? "Bulk · tote per SKU"
          : isTotePerOrder ? "Cart · tote per order"
          : session?.strategy === "discrete" ? "Order-by-order"
          : session?.strategy ?? ""
        }`}
        meta={
          <>
            <StatusPill tone="info" dot>{remaining.length} lines left</StatusPill>
            {isSingleBin && sharedToteCode && (
              <StatusPill tone="scan">Tote {sharedToteCode}</StatusPill>
            )}
            {isTotePerSku && currentSkuToteCode && (
              <StatusPill tone="scan">Tote {currentSkuToteCode}</StatusPill>
            )}
            {session?.strategy === "pick_to_bin" && current?.tote?.code && (
              <StatusPill tone="scan">Tote {current.tote.code}</StatusPill>
            )}
            {session?.strategy === "discrete" && assignedTote && (
              <StatusPill tone="scan">Tote {assignedTote}</StatusPill>
            )}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="touch" onClick={goBack}>
              {sessionUntouched ? (
                <><ArrowLeft className="h-4 w-4" /> Back</>
              ) : (
                <><Undo2 className="h-4 w-4" /> Undo session</>
              )}
            </Button>
            <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="touch">
                <ListOrdered className="h-4 w-4" /> Queue
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Upcoming picks ({remaining.length})</SheetTitle></SheetHeader>
              <ul className="mt-4 divide-y border rounded-lg overflow-hidden">
                {remaining.map((l: any, i: number) => (
                  <li key={l.id} className={`flex items-center gap-3 px-3 py-2.5 text-sm ${i === 0 ? "bg-scan/10" : ""}`}>
                    <span className="text-xs w-6 text-muted-foreground">{i + 1}</span>
                    <span className="font-mono text-xs w-20 truncate">{l.location?.code}</span>
                    <span className="flex-1 truncate font-mono">{l.sku?.sku_code}</span>
                    <span className="text-muted-foreground tabular-nums">{l.qty_picked}/{l.qty_required}</span>
                  </li>
                ))}
                {remaining.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">All done.</li>
                )}
              </ul>
            </SheetContent>
            </Sheet>
          </div>
        }
      />

      <div className="px-4 sm:px-6 py-4 max-w-3xl mx-auto space-y-5 pb-32">
        <Progress value={pct} aria-label={`${Math.round(pct)}% picked`} />

        {current ? (
          needsSharedBin ? (
            <div className="rounded-2xl border-2 border-scan/40 bg-scan/5 p-6 sm:p-8 space-y-5 shadow-sm">
              <div className="flex items-center gap-3">
                <Boxes className="h-6 w-6 text-scan" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Bulk batch · single tote
                  </div>
                  <div className="text-xl sm:text-2xl font-semibold">
                    Scan a tote for this batch
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Every order in this batch wants the same SKU. Pick all units into
                one tote — the pack station will split them by order.
              </p>
              <ScanInput
                onScan={(v) => {
                  const code = v.trim();
                  if (!code) return;
                  setBusy(true);
                  runServerFn(
                    () => assignSharedBinFn({ data: { sessionId, code } }),
                    "Assign tote",
                  )
                    .then(() => {
                      scannerBeep(true);
                      toast.success(`Tote ${code} assigned`);
                      qc.invalidateQueries({ queryKey: ["pick-session", sessionId] });
                      refetch();
                    })
                    .catch(() => scannerBeep(false))
                    .finally(() => setBusy(false));
                }}
                placeholder="Scan tote code…"
                size="lg"
                autoFocus
              />
            </div>
          ) : needsSkuTote ? (
            <div className="rounded-2xl border-2 border-scan/40 bg-scan/5 p-6 sm:p-8 space-y-5 shadow-sm">
              <div className="flex items-center gap-3">
                <Boxes className="h-6 w-6 text-scan" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Bulk batch · tote per SKU
                  </div>
                  <div className="text-xl sm:text-2xl font-semibold">
                    Scan a tote for <span className="font-mono">{currentBatch.sku?.sku_code}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Each SKU gets its own tote. All {currentBatch.qty} units of this
                SKU across {currentBatch.orderIds.size} order
                {currentBatch.orderIds.size === 1 ? "" : "s"} go into the same tote.
              </p>
              <ScanInput
                onScan={(v) => {
                  const code = v.trim();
                  if (!code) return;
                  setBusy(true);
                  runServerFn(
                    () => assignToteToSkuFn({ data: { sessionId, skuId: currentBatch.sku.id, code } }),
                    "Assign tote",
                  )
                    .then(() => {
                      scannerBeep(true);
                      toast.success(`Tote ${code} → ${currentBatch.sku?.sku_code}`);
                      qc.invalidateQueries({ queryKey: ["pick-session", sessionId] });
                      refetch();
                    })
                    .catch(() => scannerBeep(false))
                    .finally(() => setBusy(false));
                }}
                placeholder="Scan tote code…"
                size="lg"
                autoFocus
              />
            </div>
          ) : needsBatchToteSetup ? (
            <div className="rounded-2xl border-2 border-scan/40 bg-scan/5 p-6 sm:p-8 space-y-5 shadow-sm">
              <div className="flex items-center gap-3">
                <Boxes className="h-6 w-6 text-scan" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Batch pick · assign cart totes
                  </div>
                  <div className="text-xl sm:text-2xl font-semibold">
                    Scan a tote for each order
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Each order in this batch needs its own tote on your cart. Like SKUs
                will be picked together and sorted into these totes as you go.
              </p>
              <ul className="space-y-3">
                {ordersNeedingTote.map((o) => (
                  <li key={o.orderId} className="rounded-lg border bg-background p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm">{o.orderNumber ?? o.orderId.slice(0, 8)}</span>
                      <span className="text-xs text-muted-foreground">awaiting tote</span>
                    </div>
                    <ScanInput
                      onScan={(v) => {
                        if (currentOrderId !== o.orderId) {
                          // assignToteForCurrentOrder uses currentOrderId; call assignToteFn directly
                        }
                        const code = v.trim();
                        if (!code) return;
                        setBusy(true);
                        runServerFn(
                          () => assignToteFn({ data: { sessionId, orderId: o.orderId, code } }),
                          "Assign tote",
                        )
                          .then(() => {
                            setToteByOrder((m) => ({ ...m, [o.orderId]: code }));
                            scannerBeep(true);
                            toast.success(`Tote ${code} → ${o.orderNumber ?? "order"}`);
                            refetch();
                          })
                          .catch(() => scannerBeep(false))
                          .finally(() => setBusy(false));
                      }}
                      placeholder="Scan tote code…"
                      size="md"
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : needsToteAssignment ? (
            <div className="rounded-2xl border-2 border-scan/40 bg-scan/5 p-6 sm:p-8 space-y-5 shadow-sm">
              <div className="flex items-center gap-3">
                <Boxes className="h-6 w-6 text-scan" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    New order · assign tote
                  </div>
                  <div className="text-xl sm:text-2xl font-semibold">
                    Scan a tote for this order
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Each order goes into its own tote. Scan or type a tote code — all picks
                for this order will go in it, and the pack station will open this order
                when the tote is scanned.
              </p>
              <ScanInput
                onScan={assignToteForCurrentOrder}
                placeholder="Scan tote code…"
                size="lg"
                autoFocus
              />
            </div>
          ) : (
          <div className="rounded-2xl border-2 bg-card p-5 sm:p-7 space-y-6 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 sm:gap-6">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> From location
                </div>
                <div className="text-3xl sm:text-5xl font-bold font-mono leading-none">{current.location?.code ?? "—"}</div>
              </div>
              <div className="space-y-1 sm:text-right">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Pick</div>
                <div className="text-4xl sm:text-6xl font-bold tabular-nums leading-none text-scan">
                  {isBatch ? currentBatch?.qty : current.qty_required - current.qty_picked}
                </div>
                <div className="text-xs text-muted-foreground">
                  {isBatch
                    ? `${currentBatch?.orderIds.size} order${currentBatch?.orderIds.size === 1 ? "" : "s"}`
                    : `${current.qty_picked} of ${current.qty_required} done`}
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-start gap-3">
                <Package className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-xl sm:text-2xl font-semibold font-mono break-all">{current.sku?.sku_code}</div>
                  <div className="text-sm text-muted-foreground">{current.sku?.name}</div>
                  {current.lot && <div className="text-xs text-muted-foreground mt-1">Lot: {current.lot}</div>}
                </div>
              </div>
              {isTotePerOrder && currentBatch && (
                <div className="mt-3 rounded-lg border bg-muted/30 p-3 space-y-1">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Put into totes</div>
                  <ul className="text-sm">
                    {currentBatch.lines.map((l: any) => {
                      const need = l.qty_required - l.qty_picked;
                      const tote = l.tote?.code || toteByOrder[l.order_id] || "—";
                      return (
                        <li key={l.id} className="flex justify-between gap-3 py-0.5">
                          <span className="font-mono text-xs truncate">{l.order?.order_number ?? l.order_id.slice(0, 8)}</span>
                          <span className="tabular-nums">
                            <span className="text-muted-foreground">Tote </span>
                            <span className="font-mono">{tote}</span>
                            <span className="ml-2">×{need}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {(isSingleBin || isTotePerSku) && currentBatch && (
                <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-sm">
                  <span className="text-muted-foreground">Into </span>
                  <span className="font-mono">{isSingleBin ? sharedToteCode : currentSkuToteCode}</span>
                  <span className="ml-2 text-muted-foreground">· {currentBatch.qty} unit{currentBatch.qty === 1 ? "" : "s"} across {currentBatch.orderIds.size} order{currentBatch.orderIds.size === 1 ? "" : "s"}</span>
                </div>
              )}
            </div>

            {requiresTote && (
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Confirm tote</label>
                <ScanInput
                  onScan={(v) => setToteValue(v)}
                  placeholder={`Scan tote ${current.tote?.code}`}
                  size="md"
                />
                {toteValue && (
                  <div className="mt-1 text-xs">
                    {toteValue.toUpperCase() === current.tote?.code?.toUpperCase()
                      ? <span className="text-success">✓ Tote matches</span>
                      : <span className="text-destructive">✗ Mismatch — scan {current.tote?.code}</span>}
                  </div>
                )}
              </div>
            )}

            <ScanInput onScan={handleScan} placeholder="Scan SKU or barcode…" size="lg" />
          </div>
          )
        ) : (
          <div className="rounded-2xl border-2 border-success/40 bg-success/5 p-12 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 mx-auto text-success" />
            <div className="text-xl font-semibold">All picks done</div>
            <Button size="touch-lg" variant="success" onClick={complete}>Complete session</Button>
          </div>
        )}
      </div>

      {current && !needsToteAssignment && !needsBatchToteSetup && !needsSharedBin && !needsSkuTote && (
        <StickyActionBar align="between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="touch"
              onClick={() => { setShortQty(String(current.qty_required - current.qty_picked - 1)); setShortOpen(true); }}
            >
              <AlertTriangle className="h-4 w-4" /> Short
            </Button>
            <Button
              variant="outline"
              size="touch"
              onClick={() => setCountMenuOpen(true)}
              title="Cycle count this location"
            >
              <Flag className="h-4 w-4" /> Count
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="touch"
              onClick={async () => {
                const max = Math.max(...lines.map((l: any) => l.pick_sequence ?? 0));
                try {
                  await runServerFn(
                    () => bumpSeqFn({ data: { lineId: current.id, sequence: max + 1 } }),
                    "Skip line",
                  );
                  refetch();
                } catch { /* toast handled */ }
              }}
            >
              <SkipForward className="h-4 w-4" /> Skip
            </Button>
            <Button size="touch-lg" disabled={busy} onClick={() => (isBatch ? recordBatchGroup() : recordOne())}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Pick {isBatch ? currentBatch?.qty : 1} <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </StickyActionBar>
      )}

      <Dialog open={shortOpen} onOpenChange={setShortOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Short pick</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              How many units could you pick at <span className="font-mono">{current?.location?.code}</span>?
            </p>
            <Input
              type="number"
              inputMode="numeric"
              value={shortQty}
              onChange={(e) => setShortQty(e.target.value)}
              min={0}
              max={current ? current.qty_required - current.qty_picked : 0}
              className="h-12 text-2xl text-center font-bold"
              autoFocus
            />
            <Input
              placeholder="Reason (optional)"
              value={shortReason}
              onChange={(e) => setShortReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShortOpen(false)}>Cancel</Button>
            <Button variant="warning" onClick={submitShort} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Mark short
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={countMenuOpen} onOpenChange={setCountMenuOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cycle count this location</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Location <span className="font-mono">{current?.location?.code}</span>
              {current?.sku?.sku_code ? (
                <> · SKU <span className="font-mono">{current.sku.sku_code}</span></>
              ) : null}
            </p>
            <p className="text-xs text-muted-foreground">
              Keep picking — the count is queued for someone to run. Or count it
              right now: this pick session pauses on your device while you count.
            </p>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              size="touch-lg"
              className="w-full"
              onClick={() => requestCountForCurrent({ claim: true, reason: "Picker spot count" })}
            >
              <ClipboardCheck className="h-4 w-4" /> Count now
            </Button>
            <Button
              variant="outline"
              size="touch"
              className="w-full"
              onClick={() => requestCountForCurrent({ claim: false, reason: "Picker flagged at shelf" })}
            >
              <Flag className="h-4 w-4" /> Flag for later
            </Button>
            <Button variant="ghost" size="touch" className="w-full" onClick={() => setCountMenuOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
